import type { BossStatus } from '../../core/boss/boss-fight';
import type { ChatoPhase } from '../../core/boss/chato';

/**
 * Animation of JEFE-4 El Chato from the state of the core (GDD §9.2, issue #134), as a pure function: the render only reads
 * the fight and never decides a time of the game. Priority: the retreat (into his own cell), the flour reaction to a decoy
 * hit (while he is out), the stagger of the phase transition and then his state in the cycle of #133.
 */
export type Jefe4Animation = 'hidden' | 'warning' | 'out' | 'hit' | 'moto' | 'stagger' | 'retreat';

/** How long the flour reaction lasts, in ticks. */
export const HIT_TICKS = 40;

export interface Jefe4Input {
  status: BossStatus;
  /** Where he is in his cycle, or null before it has started. */
  chato: ChatoPhase | null;
  ticksSinceHit: number | null;
}

export function jefe4Animation(input: Jefe4Input): Jefe4Animation {
  if (input.status === 'retreating' || input.status === 'done') return 'retreat';
  const out = input.chato === 'OUT' || input.chato === 'MOTO';
  if (out && input.ticksSinceHit !== null && input.ticksSinceHit < HIT_TICKS) return 'hit';
  if (input.status === 'transition') return out ? 'stagger' : 'hidden';
  if (input.chato === 'WARNING') return 'warning';
  if (input.chato === 'OUT') return 'out';
  if (input.chato === 'MOTO') return 'moto';
  return 'hidden';
}

/** Whether the figure is drawn: only while he is out of his tunnel, on the motorbike or in the retreat. */
export const isFigureVisible = (animation: Jefe4Animation): boolean => animation !== 'hidden' && animation !== 'warning';

/** Phases of the comic retreat in the 0..1 progress: he goes into the tunnel, and then appears arguing in his cell. */
export const RETREAT_INTO_TUNNEL = 0.3;
export const retreatPhase = (progress: number): 'tunnel' | 'cell' => (progress < RETREAT_INTO_TUNNEL ? 'tunnel' : 'cell');
